/**
 * Externalized formatting configuration for the markdown-to-Word formatter.
 *
 * All style values live here as data, not scattered across rendering code.
 * Consumers can override any value by passing a Partial<FormatterConfig>
 * to the formatter — no code changes needed for a different document type.
 *
 * Mirrors the style constants in word_writer.py (lines 94-119).
 */

import type { LiveDocStyleSpec } from "../../api/client";
import type { NotFoundCategory } from "./types";

// ---------------------------------------------------------------------------
// Configuration interfaces
// ---------------------------------------------------------------------------

export interface FontConfig {
  /** Font family for all text. */
  name: string;
  /** Body text size in points. */
  sizeBody: number;
  /** Table cell text size in points. */
  sizeTable: number;
  /** Highlighted marker text size in points. */
  sizeHighlighted: number;
  /**
   * Body run color as a "#RRGGBB" hex string. Optional so a caller can
   * thread the destination paragraph's color through the edit-highlight
   * Approve splice; ``renderSegment`` falls back to "#000000" when unset,
   * so omitting it is byte-identical to the previous hard-coded value.
   */
  color?: string;
}

export interface HeadingConfig {
  /** Word heading level (2 or 3). */
  level: number;
  /** Font size in points. */
  fontSize: number;
  bold: boolean;
  /** Hex color string, e.g. "#000000". */
  color: string;
  /** Space before in points. */
  spaceBefore: number;
  /** Space after in points. */
  spaceAfter: number;
}

export interface TableConfig {
  /** Whether the first row is bold. */
  headerBold: boolean;
  /** Header row shading color (hex). */
  headerFill: string;
}

export interface BlockquoteConfig {
  /** Base indent in points for blockquoted content. */
  indentBase: number;
  /** Additional indent per nesting level in points. */
  indentPerLevel: number;
  /** Text color for blockquoted content (hex). */
  color: string;
}

export interface ListConfig {
  /** Base indent in points for list items. */
  indentBase: number;
  /** Additional indent per nesting level in points. */
  indentPerLevel: number;
  /** Hanging indent for bullet/number character in points (negative). */
  hangingIndent: number;
  /** Bullet character for unordered lists. */
  bulletChar: string;
  /** Space after each list item in points. */
  spaceAfter: number;
}

export interface ImageConfig {
  /** Timeout per image fetch in milliseconds. */
  fetchTimeout: number;
  /** Placeholder text when an image fails to load. */
  failurePlaceholder: string;
  /** Font color for image placeholders/captions (hex). */
  captionColor: string;
}

export interface LinkConfig {
  /** Font color for hyperlinks (hex). */
  color: string;
}

export interface FormatterConfig {
  font: FontConfig;
  headings: Record<number, HeadingConfig>;
  /** Map NOT FOUND category → Office JS highlight color name. */
  highlightColors: Record<NotFoundCategory, string>;
  /** Highlight color for [REVIEWER: ...] markers. */
  reviewerHighlight: string;
  /** Font color (hex) for [FILL: ...] placeholders. */
  fillColor: string;
  /** Demo mode: normalize [NOT FOUND - ...] to [TO FILL] without highlights. */
  demoMode: boolean;
  /** Paragraph alignment: "Justified", "Left", "Center", "Right". */
  paragraphAlignment: string;
  /** Space after body paragraphs in points. */
  paragraphSpaceAfter: number;
  /** Line spacing multiplier. */
  lineSpacing: number;
  table: TableConfig;
  blockquote: BlockquoteConfig;
  list: ListConfig;
  image: ImageConfig;
  link: LinkConfig;
}

// ---------------------------------------------------------------------------
// Default configuration — matches word_writer.py exactly
// ---------------------------------------------------------------------------

export const DEFAULT_CONFIG: FormatterConfig = {
  font: {
    name: "Times New Roman",
    sizeBody: 11,
    sizeTable: 10,
    sizeHighlighted: 10,
    color: "#000000",
  },
  headings: {
    // Markdown level → font/spacing config. Word's built-in
    // Heading{N} style still drives outline level + ToC inclusion;
    // these values just normalise the run-level appearance so the
    // doc reads consistently regardless of which template the
    // writer started from. Sizes step from 14pt at level 1 down
    // to 10pt at level 6 — same direction as Word's defaults but
    // tighter for regulatory-narrative density.
    1: {
      level: 1,
      fontSize: 14,
      bold: true,
      color: "#000000",
      spaceBefore: 14,
      spaceAfter: 6,
    },
    2: {
      level: 2,
      fontSize: 12,
      bold: true,
      color: "#000000",
      spaceBefore: 12,
      spaceAfter: 4,
    },
    3: {
      level: 3,
      fontSize: 11,
      bold: true,
      color: "#000000",
      spaceBefore: 10,
      spaceAfter: 4,
    },
    4: {
      level: 4,
      fontSize: 11,
      bold: true,
      color: "#000000",
      spaceBefore: 8,
      spaceAfter: 3,
    },
    5: {
      level: 5,
      fontSize: 10,
      bold: true,
      color: "#000000",
      spaceBefore: 6,
      spaceAfter: 3,
    },
    6: {
      level: 6,
      fontSize: 10,
      bold: true,
      color: "#000000",
      spaceBefore: 6,
      spaceAfter: 2,
    },
  },
  // Subtle, professional highlight colors — regulatory documents
  // should not scream with magenta/red. Light gray italic for all
  // NOT FOUND variants, soft blue for reviewer notes.
  highlightColors: {
    "NOT IN SOURCE": "LightGray",
    "RETRIEVAL FAILURE": "LightGray",
    "AWAITING DOCUMENT": "LightGray",
  },
  reviewerHighlight: "LightGray",
  fillColor: "#999999",
  demoMode: false,
  paragraphAlignment: "Justified",
  paragraphSpaceAfter: 6,
  lineSpacing: 1.15,
  table: {
    headerBold: true,
    headerFill: "#D9D9D9",
  },
  blockquote: {
    indentBase: 36,
    indentPerLevel: 18,
    color: "#444444",
  },
  list: {
    indentBase: 18,
    indentPerLevel: 18,
    hangingIndent: -12,
    bulletChar: "•",
    spaceAfter: 2,
  },
  image: {
    fetchTimeout: 5000,
    failurePlaceholder: "[Image could not be loaded]",
    captionColor: "#808080",
  },
  link: {
    color: "#0563C1",
  },
};

// ---------------------------------------------------------------------------
// Client-template look — the live document's active style spec
// ---------------------------------------------------------------------------

/** Formatter values a client template's style spec overrides. Pure. Returns
 *  `{}` for the global spec (no `_template`), so public doctypes keep exactly
 *  DEFAULT_CONFIG. Marker highlight colours are never touched. */
export function formatterOverridesFromStyleSpec(spec: LiveDocStyleSpec | null | undefined): Partial<FormatterConfig> {
  if (!spec?._template) return {};
  const out: Partial<FormatterConfig> = {
    font: {
      ...DEFAULT_CONFIG.font,
      name: spec.normal.font,
      sizeBody: spec.normal.size_pt,
      ...(spec.table?.font_size_pt !== undefined ? { sizeTable: spec.table.font_size_pt } : {}),
    },
    paragraphSpaceAfter: spec.normal.space_after_pt,
    lineSpacing: spec.normal.line_spacing_multiple ?? spec.normal.line_spacing_pt / 12,
  };
  if (spec.normal.alignment) {
    out.paragraphAlignment = {
      left: "Left",
      center: "Center",
      right: "Right",
      justified: "Justified",
    }[spec.normal.alignment];
  }
  if (spec.table) {
    out.table = {
      headerBold: spec.table.header_bold ?? DEFAULT_CONFIG.table.headerBold,
      headerFill: spec.table.header_fill ? `#${spec.table.header_fill}` : DEFAULT_CONFIG.table.headerFill,
    };
  }
  return out;
}

let activeOverrides: Partial<FormatterConfig> = {};
let activeSpec: LiveDocStyleSpec | null = null;

/** Record the spec the live document was last normalized to (wordWriter's
 *  style normalize), so content inserted afterwards follows it. */
export function setActiveStyleSpec(spec: LiveDocStyleSpec | null | undefined): void {
  activeOverrides = formatterOverridesFromStyleSpec(spec);
  activeSpec = spec ?? null;
}

export interface SubheadingLook {
  font: string;
  sizePt: number;
  bold: boolean;
  italic: boolean;
  colorHex: string;
}

/** Heading levels of src/csr_sim/live_doc_style_spec.json — the look the
 *  scaffold gives skeleton headings — for when no spec has been applied. */
const _DEFAULT_HEADING_SIZES: Record<number, number> = { 1: 14, 2: 12, 3: 11, 4: 11, 5: 11 };

/**
 * Run-level look of an in-body subheading at Word heading ``level``, as
 * direct formatting. A live doc scaffolded for live drafting never has its
 * built-in HeadingN styles normalized (only a Load does), so a bare
 * ``Heading2`` rendered in Word's default blue theme font next to the
 * scaffold's black Times New Roman headings. Follows the active style spec
 * (a client template's heading look) when one is set; levels deeper than the
 * spec lists take its deepest one. Pure — Word-free.
 */
export function subheadingLook(level: number): SubheadingLook {
  const spec = activeSpec;
  if (spec && spec.headings.length > 0) {
    const sorted = [...spec.headings].sort((a, b) => a.level - b.level);
    const h = sorted.find((x) => x.level === level) ?? (level < sorted[0].level ? sorted[0] : sorted[sorted.length - 1]);
    return {
      font: h.font ?? spec.heading_common.font,
      sizePt: h.size_pt,
      bold: h.bold ?? spec.heading_common.bold,
      italic: h.italic ?? false,
      colorHex: `#${h.color ?? spec.heading_common.color}`,
    };
  }
  return {
    font: DEFAULT_CONFIG.font.name,
    sizePt: _DEFAULT_HEADING_SIZES[Math.min(Math.max(level, 1), 5)],
    bold: true,
    italic: false,
    colorHex: "#000000",
  };
}

/**
 * Merge a partial config override with the default config.
 * Shallow merge at the top level, deep merge for nested objects. The active
 * client-template look (see {@link setActiveStyleSpec}) sits between the
 * defaults and the caller's overrides.
 */
export function mergeConfig(overrides?: Partial<FormatterConfig>): FormatterConfig {
  if (!overrides && Object.keys(activeOverrides).length === 0) return DEFAULT_CONFIG;
  const base = _merge(DEFAULT_CONFIG, activeOverrides);
  if (!overrides) return base;
  return _merge(base, overrides);
}

function _merge(base: FormatterConfig, overrides: Partial<FormatterConfig>): FormatterConfig {
  return {
    ...base,
    ...overrides,
    font: { ...base.font, ...overrides.font },
    headings: { ...base.headings, ...overrides.headings },
    highlightColors: {
      ...base.highlightColors,
      ...overrides.highlightColors,
    },
    table: { ...base.table, ...overrides.table },
    blockquote: { ...base.blockquote, ...overrides.blockquote },
    list: { ...base.list, ...overrides.list },
    image: { ...base.image, ...overrides.image },
    link: { ...base.link, ...overrides.link },
  };
}
